import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { ROLE_LABELS, ALL_ROLES, STORE_SUPER, isSuperStore } from "../lib/states";
import { UserPlus, Loader2, ShieldCheck, X, Pencil, Trash2 } from "lucide-react";

const EMPTY = { name: "", email: "", phone: "", role: "backend_staff", password: "", store: STORE_SUPER };

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [stores, setStores] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // user object being edited, or null
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null); // user pending delete

  const meIsSuper = isSuperStore(me);
  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => {
    load();
    if (meIsSuper) api.get("/stores").then((r) => setStores(r.data || [])).catch(() => {});
  }, [meIsSuper]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name, email: u.email, phone: u.phone || "", role: u.role, password: "", store: u.store || STORE_SUPER });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing) {
        const payload = { name: form.name, email: form.email, phone: form.phone, role: form.role };
        if (form.password) payload.password = form.password;
        if (meIsSuper) payload.store = form.store;
        await api.patch(`/users/${editing.id}`, payload);
        toast.success(`Updated ${form.name}`);
      } else {
        const payload = { ...form };
        if (!meIsSuper) delete payload.store;
        await api.post("/users", payload);
        toast.success(`Created ${form.name}`);
      }
      setOpen(false);
      setForm(EMPTY);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (u) => {
    try { await api.patch(`/users/${u.id}`, { active: !u.active }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const doDelete = async (u) => {
    setBusy(true);
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(`Deleted ${u.name}`);
      setConfirmDel(null);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      <div className="h-16 shrink-0 px-6 flex items-center justify-between border-b border-stone-200">
        <div>
          <h1 className="font-heading text-xl tracking-tight text-stone-900">Users</h1>
          <p className="text-xs text-stone-500">{users.length} accounts · admin-managed</p>
        </div>
        <button onClick={openCreate} data-testid="add-user-button"
          className="flex items-center gap-2 bg-stone-900 hover:bg-stone-800 text-white text-sm px-4 py-2 rounded-md transition-colors">
          <UserPlus className="h-4 w-4" /> New user
        </button>
      </div>

      <div className="p-6">
        <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs tracking-[0.15em] uppercase text-stone-400 border-b border-stone-200">
                <th className="px-5 py-3">Name</th><th className="px-5 py-3">Email</th>
                <th className="px-5 py-3">Phone</th><th className="px-5 py-3">Role</th>
                {meIsSuper && <th className="px-5 py-3">Store</th>}
                <th className="px-5 py-3">Status</th><th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="users-table">
              {users.map((u) => (
                <tr key={u.id} className="border-b border-stone-100 hover:bg-stone-50">
                  <td className="px-5 py-3 font-medium text-stone-800 flex items-center gap-2">
                    {u.role === "admin" && <ShieldCheck className="h-4 w-4 text-stone-500" />}{u.name}
                  </td>
                  <td className="px-5 py-3 text-stone-600">{u.email}</td>
                  <td className="px-5 py-3 text-stone-600">{u.phone || "—"}</td>
                  <td className="px-5 py-3 text-stone-600">{ROLE_LABELS[u.role] || u.role}</td>
                  {meIsSuper && (
                    <td className="px-5 py-3" data-testid={`user-store-${u.id}`}>
                      {(u.store || STORE_SUPER) === STORE_SUPER ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-stone-900 text-stone-100 font-medium tracking-wide uppercase">{STORE_SUPER}</span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200 font-medium">{u.store}</span>
                      )}
                    </td>
                  )}
                  <td className="px-5 py-3">
                    {u.role === "admin" ? <span className="text-xs text-stone-400">—</span> : (
                      <button onClick={() => toggleActive(u)} data-testid={`toggle-active-${u.id}`}
                        className={`text-xs px-2.5 py-1 rounded-full border font-medium ${u.active !== false ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-stone-100 text-stone-500 border-stone-300"}`}>
                        {u.active !== false ? "Active" : "Inactive"}
                      </button>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`} title="Edit"
                        className="p-1.5 rounded-md text-stone-500 hover:bg-stone-100 hover:text-stone-800 transition-colors">
                        <Pencil className="h-4 w-4" />
                      </button>
                      {u.id !== me?.id && (
                        <button onClick={() => setConfirmDel(u)} data-testid={`delete-user-${u.id}`} title="Delete"
                          className="p-1.5 rounded-md text-stone-500 hover:bg-red-50 hover:text-red-600 transition-colors">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-stone-900/40 animate-fade-in" onClick={() => setOpen(false)} />
          <form onSubmit={submit} data-testid="user-form" className="relative bg-white rounded-xl border border-stone-200 shadow-2xl w-full max-w-md p-6 animate-fade-in">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading text-xl tracking-tight text-stone-900">{editing ? "Edit user" : "New user"}</h2>
              <button type="button" onClick={() => setOpen(false)} className="p-1 rounded hover:bg-stone-100 text-stone-500"><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-3">
              {[["name", "Name", "text"], ["email", "Email", "email"], ["phone", "Phone", "text"], ["password", editing ? "New password (leave blank to keep)" : "Temp password", "text"]].map(([k, label, type]) => (
                <div key={k}>
                  <label className="text-xs tracking-[0.15em] uppercase font-semibold text-stone-400">{label}</label>
                  <input type={type} required={k !== "phone" && !(editing && k === "password")} value={form[k]} onChange={(e) => setForm((p) => ({ ...p, [k]: e.target.value }))}
                    data-testid={`user-${k}`} className="mt-1 w-full border border-stone-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/60" />
                </div>
              ))}
              <div>
                <label className="text-xs tracking-[0.15em] uppercase font-semibold text-stone-400">Role</label>
                <select value={form.role} onChange={(e) => setForm((p) => ({ ...p, role: e.target.value }))} data-testid="user-role"
                  className="mt-1 w-full border border-stone-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/60">
                  {ALL_ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                </select>
              </div>
              {meIsSuper && (
                <div>
                  <label className="text-xs tracking-[0.15em] uppercase font-semibold text-stone-400">Store</label>
                  <select value={form.store} onChange={(e) => setForm((p) => ({ ...p, store: e.target.value }))} data-testid="user-store"
                    className="mt-1 w-full border border-stone-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/60">
                    <option value={STORE_SUPER}>{STORE_SUPER} (sees everything + AI)</option>
                    {stores.filter((s) => s.name !== STORE_SUPER).map((s) => (
                      <option key={s.id} value={s.name}>{s.name}</option>
                    ))}
                  </select>
                  <p className="text-[11px] text-stone-400 mt-1">Store-scoped users see only leads assigned to their store and don&apos;t see AI features.</p>
                </div>
              )}
            </div>
            <button type="submit" disabled={busy} data-testid="user-submit"
              className="mt-5 w-full bg-stone-900 hover:bg-stone-800 text-white rounded-md py-2.5 font-medium flex items-center justify-center gap-2 disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? "Save changes" : "Create user")}
            </button>
          </form>
        </div>
      )}

      {confirmDel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-stone-900/40 animate-fade-in" onClick={() => setConfirmDel(null)} />
          <div data-testid="delete-confirm" className="relative bg-white rounded-xl border border-stone-200 shadow-2xl w-full max-w-sm p-6 animate-fade-in">
            <div className="flex items-center gap-2 mb-2">
              <Trash2 className="h-5 w-5 text-red-500" />
              <h2 className="font-heading text-lg tracking-tight text-stone-900">Delete user</h2>
            </div>
            <p className="text-sm text-stone-600">Delete <span className="font-medium">{confirmDel.name}</span>? Their account is removed and any leads assigned to them are unassigned. This cannot be undone.</p>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setConfirmDel(null)} className="flex-1 border border-stone-300 rounded-md py-2 text-sm text-stone-700 hover:bg-stone-50">Cancel</button>
              <button onClick={() => doDelete(confirmDel)} disabled={busy} data-testid="confirm-delete-button"
                className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-md py-2 text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-60">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
